import React, { useEffect, useState, useMemo } from 'react';
import { updateDeviceModel } from '../../utils/deviceService';
import { API_config } from '../../config/apiConfig';
import '../../pages/Admin/Admin.styles.css';

const CarList = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [deviceModels, setDeviceModels] = useState([]); // Stores available models (objects: name, icon, color)
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [searchQuery, setSearchQuery] = useState('');
    const [countdown, setCountdown] = useState(5); // Countdown timer for next refresh

    // Credentials & API Config
    const authHeader = API_config.authHeader;
    const apiUrl = API_config.apiUrl;

    useEffect(() => {
        loadConfigAndModels();
        loadDevices();

        // Reset countdown when component mounts
        setCountdown(5);

        // Update current time every second for real-time status updates
        const timeInterval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);

        // Update countdown every second
        const countdownInterval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    return 5; // Reset to 5 when it reaches 0
                }
                return prev - 1;
            });
        }, 1000);

        // Refresh device data every 5 seconds
        const refreshInterval = setInterval(() => {
            loadDevices(true); // Pass true to indicate background refresh
            setCountdown(5); // Reset countdown after refresh
        }, 5000);

        return () => {
            clearInterval(timeInterval);
            clearInterval(countdownInterval);
            clearInterval(refreshInterval);
        };
    }, []);

    const loadConfigAndModels = async () => {
        try {
            // 1. Fetch config to get models URL
            const configResp = await fetch(`${process.env.PUBLIC_URL || ''}/config.json`);
            if (!configResp.ok) throw new Error('Failed to load config');
            const config = await configResp.json();

            if (config?.deviceModelsUrl) {
                // 2. Fetch models list
                const modelsResp = await fetch(`${process.env.PUBLIC_URL || ''}/${config.deviceModelsUrl}`);
                if (!modelsResp.ok) throw new Error('Failed to load device models');
                const data = await modelsResp.json();

                // Parse models structure (array of strings or objects)
                const modelsArrayRaw = Array.isArray(data) ? data : data?.models;
                const modelsArray = Array.isArray(modelsArrayRaw) ? modelsArrayRaw : [];
                const parsedModels = [];

                modelsArray.forEach(m => {
                    if (typeof m === 'string') {
                        if (m !== 'Warning') parsedModels.push({ name: m, icon: '', color: '#ffffff' });
                    } else if (m && typeof m === 'object' && m.name !== undefined) {
                        if (m.name !== 'Warning') parsedModels.push(m);
                    }
                });
                setDeviceModels(parsedModels);
            }
        } catch (err) {
            console.error('Error loading models:', err);
        }
    };

    const loadDevices = async (isBackgroundRefresh = false) => {
        if (!isBackgroundRefresh) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        try {
            const response = await fetch(`${apiUrl}/devices`, {
                headers: { 'Authorization': authHeader },
            });
            if (!response.ok) throw new Error('Failed to fetch devices');
            const data = await response.json();
            setDevices(data);
        } catch (err) {
            if (!isBackgroundRefresh) {
                setError(err.message);
            }
            console.error('Error loading devices:', err);
        } finally {
            if (!isBackgroundRefresh) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    };

    const handleModelChange = async (device, newModelName) => {
        // Optimistic update
        const updatedDevices = devices.map(d =>
            d.id === device.id ? { ...d, model: newModelName, deviceModel: newModelName } : d
        );
        setDevices(updatedDevices);

        try {
            await updateDeviceModel(device.id, newModelName);
        } catch (err) {
            console.error('Failed to update model', err);
            loadDevices(); // Revert
            alert('Failed to update model');
        }
    };

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    };

    // Helper to check if device is online (last message within 5 seconds)
    const isDeviceOnline = (lastUpdate) => {
        if (!lastUpdate) return false;
        const lastUpdateTime = new Date(lastUpdate).getTime();
        const timeDiff = currentTime - lastUpdateTime;
        return timeDiff <= 5000; // 5 seconds in milliseconds
    };

    // Helper to get model data for a device
    const getModelData = (modelName) => {
        return deviceModels.find(m => m.name === modelName) || { icon: '', color: '' };
    };

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    // Helper: Get Device Number
    const getDeviceNumber = (name) => {
        if (!name) return '';
        const parts = name.split(' - ');
        return parts.length > 1 ? parts[0] : '';
    };

    // Sorting Logic
    const sortedDevices = useMemo(() => {
        if (!devices) return [];
        let sortableItems = [...devices];

        // First filter
        if (searchQuery) {
            sortableItems = sortableItems.filter(device =>
                device.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                device.uniqueId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                device.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                device.id?.toString().includes(searchQuery)
            );
        }

        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue, bValue;

                // Determine values based on sort key
                switch (sortConfig.key) {
                    case 'number':
                        aValue = getDeviceNumber(a.name);
                        bValue = getDeviceNumber(b.name);
                        // Try numeric sort for Number column
                        const aNum = parseFloat(aValue);
                        const bNum = parseFloat(bValue);
                        if (!isNaN(aNum) && !isNaN(bNum)) {
                            aValue = aNum;
                            bValue = bNum;
                        }
                        break;
                    case 'status':
                        aValue = isDeviceOnline(a.lastUpdate) ? 'online' : 'offline';
                        bValue = isDeviceOnline(b.lastUpdate) ? 'online' : 'offline';
                        break;
                    case 'lastUpdate':
                        aValue = new Date(a.lastUpdate || 0).getTime();
                        bValue = new Date(b.lastUpdate || 0).getTime();
                        break;
                    case 'model':
                        aValue = a.model || a.deviceModel || '';
                        bValue = b.model || b.deviceModel || '';
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                        // Handle null/undefined
                        if (aValue === undefined || aValue === null) aValue = '';
                        if (bValue === undefined || bValue === null) bValue = '';
                        // Case insensitive string sort
                        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [devices, sortConfig, searchQuery, currentTime]); // Add currentTime to re-sort status if needed

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (name) => {
        const isActive = sortConfig.key === name;
        return (
            <span
                className={`material-icons-outlined sort-icon ${isActive ? 'active' : ''}`}
                style={{ fontSize: '16px', marginLeft: '4px', opacity: isActive ? 1 : 0.3, transition: 'opacity 0.2s' }}
            >
                {isActive && sortConfig.direction === 'descending' ? 'arrow_downward' : 'arrow_upward'}
            </span>
        );
    };

    const getHeaderStyle = () => ({
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    });

    if (loading) return (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            Loading devices...
        </div>
    );

    if (error) return (
        <div style={{ padding: '20px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px' }}>
            Error: {error}
        </div>
    );

    return (
        <>
            {/* Table Container */}
            <div className="admin-table-container">
                {/* Table Header */}
                <div className="admin-table-header">
                    <div className="admin-table-search-wrapper">
                        <div className="admin-search-input-wrapper">
                            <span className="material-icons-outlined">search</span>
                            {/* Note: Filtering is handled in useMemo now */}
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder="Search by Name, IMEI, or Phone..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="admin-table-sort" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {/* Refresh Countdown Timer */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                            {refreshing ? (
                                <>
                                    <span className="material-icons-outlined" style={{ fontSize: '16px', animation: 'spin 1s linear infinite' }}>
                                        sync
                                    </span>
                                    <span>Refreshing...</span>
                                </>
                            ) : (
                                <>
                                    <span className="material-icons-outlined" style={{ fontSize: '16px' }}>
                                        schedule
                                    </span>
                                    <span>Next refresh in {countdown}s</span>
                                </>
                            )}
                        </div>
                        <span className="admin-table-sort-label">Total:</span>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {sortedDevices.length} devices
                        </span>
                    </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('number')}>
                                    <div style={getHeaderStyle()}>Number {getSortIcon('number')}</div>
                                </th>
                                <th onClick={() => requestSort('status')}>
                                    <div style={getHeaderStyle()}>Status {getSortIcon('status')}</div>
                                </th>
                                <th onClick={() => requestSort('id')}>
                                    <div style={getHeaderStyle()}>ID {getSortIcon('id')}</div>
                                </th>
                                <th onClick={() => requestSort('name')}>
                                    <div style={getHeaderStyle()}>Name {getSortIcon('name')}</div>
                                </th>
                                <th onClick={() => requestSort('uniqueId')}>
                                    <div style={getHeaderStyle()}>IMEI {getSortIcon('uniqueId')}</div>
                                </th>
                                <th onClick={() => requestSort('phone')}>
                                    <div style={getHeaderStyle()}>Phone {getSortIcon('phone')}</div>
                                </th>
                                <th onClick={() => requestSort('lastUpdate')}>
                                    <div style={getHeaderStyle()}>Last Online {getSortIcon('lastUpdate')}</div>
                                </th>
                                <th onClick={() => requestSort('model')}>
                                    <div style={getHeaderStyle()}>Model {getSortIcon('model')}</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedDevices.length > 0 ? (
                                sortedDevices.map(device => {
                                    const currentModelName = device.model || device.deviceModel;
                                    const modelData = getModelData(currentModelName);
                                    const online = isDeviceOnline(device.lastUpdate);

                                    return (
                                        <tr key={device.id}>
                                            <td className="admin-table-mono" style={{ fontWeight: 600 }}>
                                                {getDeviceNumber(device.name)}
                                            </td>
                                            <td>
                                                <span className={`admin-table-status-badge ${online ? 'online' : 'offline'}`}>
                                                    <span className="status-dot"></span>
                                                    {online ? 'Online' : 'Offline'}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ fontWeight: 500 }}>{device.id}</span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {modelData.icon && (
                                                        <img
                                                            src={`${process.env.PUBLIC_URL || ''}/${modelData.icon}`}
                                                            alt="icon"
                                                            style={{ width: '24px', height: '24px' }}
                                                        />
                                                    )}
                                                    <span className="admin-table-device-name">{device.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="admin-table-mono">
                                                    {device.uniqueId || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="admin-table-mono">
                                                    {device.phone || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="admin-table-time">
                                                    <span className="admin-table-time-primary">
                                                        {device.lastUpdate ? new Date(device.lastUpdate).toLocaleTimeString() : 'Never'}
                                                    </span>
                                                    <span className="admin-table-time-secondary">
                                                        {device.lastUpdate ? new Date(device.lastUpdate).toLocaleDateString() : ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <select
                                                    value={currentModelName || ''}
                                                    onChange={(e) => handleModelChange(device, e.target.value)}
                                                    style={{
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        border: '1px solid var(--border-color)',
                                                        backgroundColor: 'var(--bg-tertiary)',
                                                        color: 'var(--text-primary)',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        minWidth: '100px'
                                                    }}
                                                >
                                                    <option value="">Default</option>
                                                    {deviceModels.map(m => (
                                                        <option key={m.name} value={m.name}>{m.name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                                        {searchQuery ? 'No devices found matching your search' : 'No devices available'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add spin animation for refresh indicator */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
};

export default CarList;

